import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { createSelector } from 'reselect';
import { Empty } from '../google/protobuf/empty_pb';
import {
  AuthServiceClient,
  LoginWebRequest,
  RefreshTokenRequest,
  ValidateCodeRequest,
  ChangePasswordRequest,
  RefreshAccessRequest,
  LoginUserRequest,
  AccountEmailAuthRequest,
  EmailAuthCodeRequest,
  LoginViaTokenRequest,
} from '../proto/authpb/auth_grpc_web_pb';

import { auth } from '../lib/auth/Auth';

const tokenKey = 'id_token';
const refreshTokenKey = 'refresh_token';
const STORAGE_LOGOUT_MESSAGE = 'STORAGE_LOGOUT_MESSAGE';

const serviceNoAuth = new AuthServiceClient(window.env.GRPC_ENDPOINT);
let userAccess = undefined;

export function getToken() {
  return localStorage.getItem(tokenKey);
}

export function getLogoutMessage() {
  return localStorage.getItem(STORAGE_LOGOUT_MESSAGE);
}

export function removeLoginMessage() {
  return localStorage.removeItem(STORAGE_LOGOUT_MESSAGE);
}

export function getCurrentUser() {
  try {
    const jwt = getToken();
    const jwtDecodedData = jwtDecode(jwt);

    localStorage.setItem('account_id', jwtDecodedData.AccountId);

    return jwtDecodedData;
  } catch (error) {
    return null;
  }
}

export function getUserAccess() {
  refreshAccessPromise();

  const access = localStorage.getItem('access');
  userAccess = JSON.parse(access);
  return userAccess;
}

export const refreshAccessPromise = createSelector(
  () =>
    (async () => {
      const userAccessesList = await refreshAccess();
      return userAccessesList;
    })(),
  (userAccessesList) => userAccessesList
);

export function refreshToken(auth) {
  //if user not logged in then return
  const user = getCurrentUser();
  if (!user) return;

  //if token already expired then return
  const unixNow = new Date().getTime() / 1000;
  if (unixNow > user.exp) return;

  //if token will not expire in 1hr then return
  const refreshTime = moment(new Date()).add(60, 'm').toDate();
  const refreshUnix = refreshTime.getTime() / 1000;
  if (refreshUnix < user.exp) return;

  //if token will about to expire then request a new one
  let req = new RefreshTokenRequest();
  req.setRefreshToken(localStorage.getItem(refreshTokenKey));
  req.setClientId(window.env.GRPC_CLIENT_ID);

  const service = new AuthServiceClient(window.env.GRPC_ENDPOINT, {}, { ...auth });

  service.refreshToken(req, {}, (error, response) => {
    if (error) {
      console.error('refreshToken', error);
    } else {
      const { accessToken, refreshToken, userAccessesList } = response.toObject();
      SetTokens(accessToken, refreshToken, userAccessesList);
    }
  });
}

export async function refreshAccess() {
  console.log('refresh access attempt - updated ui');

  //if user not logged in then return
  const user = getCurrentUser();
  if (!user) return;
  //if token already expired then return
  const unixNow = new Date().getTime() / 1000;
  if (unixNow > user.exp) return;
  //do not request if expiration is more than a minute
  //if (user.exp - unixNow > 360) return;

  const service = new AuthServiceClient(window.env.GRPC_ENDPOINT, {}, { ...auth });

  //if token will about to expire then request a new one
  return new Promise((resolve, reject) => {
    let req = new RefreshAccessRequest();
    req.setUsrId(user.UserId);

    service.refreshAccess(req, {}, (error, response) => {
      if (error) {
        console.error('refreshToken', error);
      } else {
        const { userAccessesList } = response.toObject();
        setAccess(userAccessesList);
        resolve(userAccessesList);
      }
    });
  });
}

function setAccess(userAccessesList) {
  let access = {};
  for (let i = 0; i < userAccessesList.length; i++) {
    const a = userAccessesList[i];
    if (a.access) {
      const key = (a.menu + a.subMenu + a.pageName).replace(/ /g, '');
      access[key] = a.access;
    }
  }

  localStorage.setItem('pages', JSON.stringify(userAccessesList));
  localStorage.setItem('access', JSON.stringify(access));
}

export async function loginWeb(data) {
  return new Promise((resolve, reject) => {
    let req = new LoginWebRequest();
    req.setEmail(data.email);
    req.setPassword(data.password);
    req.setAuthenticationMode(data.authenticationMode);
    req.setCorrespondent(data.correspondent);
    req.setClientId(window.env.GRPC_CLIENT_ID);

    serviceNoAuth.loginWeb(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function loginuser(data) {
  return new Promise((resolve, reject) => {
    let req = new LoginUserRequest();
    req.setIUser(data.i_user);
    req.setIToken(data.i_token);
    req.setClientId(window.env.GRPC_CLIENT_ID);

    serviceNoAuth.loginuser(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        const { accessToken, refreshToken, userAccessesList } = response.toObject();
        SetTokens(accessToken, refreshToken, userAccessesList);
        resolve();
      }
    });
  });
}

export function SetTokens(accessToken, refreshToken, userAccessesList) {
  localStorage.setItem(tokenKey, accessToken);
  localStorage.setItem(refreshTokenKey, refreshToken);

  let access = {};

  for (let i = 0; i < userAccessesList?.length; i++) {
    const a = userAccessesList[i];
    if (a.access) {
      const key = (a.menu + a.subMenu + a.pageName).replace(/ /g, '');
      access[key] = a.access;
    }
  }

  localStorage.setItem('pages', JSON.stringify(userAccessesList));
  localStorage.setItem('access', JSON.stringify(access));
}

const logoutUser = (message) => {
  sessionStorage.clear();
  localStorage.clear();

  if (message && !message.includes('Logged Out')) {
    if (message.includes('token is expired by')) {
      localStorage.setItem(STORAGE_LOGOUT_MESSAGE, 'session logged out.');
    } else {
      localStorage.setItem(STORAGE_LOGOUT_MESSAGE, message);
    }
  }

  if (window.location.pathname !== '/login') {
    window.location = '/';
  }
};
export function logout(message) {
  const service = new AuthServiceClient(window.env.GRPC_ENDPOINT, {}, { ...auth });
  const req = new Empty();

  if (message?.includes('access is invalid') || !getToken()) {
    return logoutUser(message);
  }

  service.logout(req, {}, (error) => {
    if (error) {
      console.error(error);
    } else {
      logoutUser(message);
    }
  });
}

export function getPages() {
  const lsp = localStorage.getItem('pages');
  const pages = JSON.parse(lsp);
  return pages;
}

export function validateAuthCode(param) {
  return new Promise((resolve, reject) => {
    let req = new ValidateCodeRequest();
    req.setEmail(param.email);
    req.setPassword(param.password);
    req.setCode(param.authCode);
    req.setAuthenticationMode(param.authenticationMode);
    req.setSessionKey(param.sessionKey);
    req.setClientId(window.env.GRPC_CLIENT_ID);
    req.setCorrespondent(param.correspondent);

    serviceNoAuth.validateCode(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        const { accessToken, refreshToken, userAccessesList } = response.toObject();
        SetTokens(accessToken, refreshToken, userAccessesList);
        resolve();
      }
    });
  });
}

export function changePassword(param) {
  const service = new AuthServiceClient(window.env.GRPC_ENDPOINT, {}, { ...auth });

  return new Promise((resolve, reject) => {
    let req = new ChangePasswordRequest();
    req.setUsrId(param.usrId);
    req.setLastPassword(param.lastPassword);
    req.setNewPassword(param.newPassword);
    req.setSecurityWordHint(param.securityWordHint);
    req.setSecurityWord(param.securityWord);

    service.changePassword(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function accountEmailAuth(accountId, email) {
  return new Promise((resolve, reject) => {
    const req = new AccountEmailAuthRequest();
    req.setAccountId(accountId);
    req.setEmail(email);

    serviceNoAuth.accountEmailAuth(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export async function emailAuthCode(email) {
  return new Promise((resolve, reject) => {
    const req = new EmailAuthCodeRequest();
    req.setEmail(email);

    serviceNoAuth.emailAuthCode(req, {}, (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response.toObject());
      }
    });
  });
}

export function getAccess(component, userType) {
  if (component === 'master')
    return !(
      userType === 'Master Account' ||
      userType === 'User' ||
      userType === 'Counterparty'
    );
  if (component === 'correspondent')
    return !(userType === 'Counterparty' || userType === 'User');
  if (component === 'account_no') return !(userType === 'User');
  return true;
}

const authServices = {
  getCurrentUser,
  refreshAccess,
  getUserAccess,
  getPages,
  changePassword,
  getAccess,
  refreshAccessPromise,
};

export default authServices;
